'use client'

import { Avatar, Button, DropdownMenu, Icon, SearchBar } from '@graftcode/design-system'

import { HEADER_TEST_IDS } from '@common/common-test-ids.const'
import { Logo } from '@common/components/Logo'
import { Selector } from '@common/components/Selector'

import { HelpDrawer } from '../HelpDrawer'
import { ShareFeedback } from '../ShareFeedback'

import type { HeaderProps } from './Header.types'
import { useHeaderDefault } from './hooks/useHeaderDefault'

import styles from './Header.module.scss'

export function HeaderDefault(props: HeaderProps) {
  const {
    helpItems,
    leftSlot,
    rightSlot,
    hideActions = false,
    projectSelectorLoading = false,
    projectSelectorDisabled = false,
    comingSoonLabel,
    onProfileClick,
    onHelpClick,
    onProjectSelect,
    onNotificationClick,
    onMenuToggle,
    searchSlot,
    as = 'div',
    rootClassName,
    notificationBadge,
  } = props

  const {
    currentProject,
    feedbackPopoverOpen,
    handleFeedbackPopoverOpenChange,
    handleSearchChange,
    handleSearchClear,
    hasHelpItems,
    isHelpButtonDisabled,
    isHelpDrawerOpen,
    isHelpDropdownActive,
    loadedAvatarSrc,
    showHeaderAvatar,
    notificationsDisabled,
    projectItems,
    resolvedHelpItems,
    searchDisabled: isSearchDisabled,
    searchValue,
    setIsHelpDrawerOpen,
    updatedProfileItems,
  } = useHeaderDefault(props)

  const Root = as === 'header' ? 'header' : 'div'
  const rootClassNames = [styles.wrapper, rootClassName].filter(Boolean).join(' ')
  const handleDrawerToggle = () => onMenuToggle?.()
  const searchbarClasses = [styles.searchbar, isSearchDisabled && styles.disabled].filter(Boolean).join(' ')
  const helpDesktopClasses = [styles.helpDesktop, isHelpButtonDisabled && styles.disabled].filter(Boolean).join(' ')
  const helpMobileClasses = [styles.helpMobile, isHelpButtonDisabled && styles.disabled].filter(Boolean).join(' ')
  const notificationClasses = [styles.notification, notificationsDisabled && styles.disabled].filter(Boolean).join(' ')
  const searchButtonClasses = [styles.search, isSearchDisabled && styles.disabled].filter(Boolean).join(' ')

  return (
    <Root className={rootClassNames} data-testid={HEADER_TEST_IDS.root}>
      <div className={styles.topbarLeft}>
        <a href="/" className={styles.logo}>
          <Logo />
        </a>

        {leftSlot ?? (
          <div className={styles.selector} data-testid={HEADER_TEST_IDS.projectSelector}>
            <Selector
              selectedName={currentProject?.name || 'Select Project'}
              items={projectItems}
              loading={projectSelectorLoading}
              disabled={projectSelectorDisabled}
              onSelect={onProjectSelect}
            />
          </div>
        )}
      </div>

      {hideActions && (rightSlot || onMenuToggle || searchSlot) && (
        <div className={styles.topbarRight}>
          {searchSlot ? <div className={styles.searchSlotWrap}>{searchSlot}</div> : null}
          {rightSlot}
          {onMenuToggle ? (
            <div className={styles.actions}>
              <div className={styles.navigation}>
                <Button
                  iconOnly
                  size="l"
                  variant="ghost"
                  iconPlacement="right"
                  icon={Icon.NavigationRegular}
                  onClick={handleDrawerToggle}
                />
              </div>
            </div>
          ) : null}
        </div>
      )}

      {!hideActions ? (
        <div className={styles.topbarRight}>
          {searchSlot ? (
            <div className={styles.searchSlotWrap}>{searchSlot}</div>
          ) : (
            <div className={searchbarClasses}>
              <SearchBar
                size="md"
                shortcut="⌘N"
                version="white"
                value={searchValue}
                onChange={handleSearchChange}
                onClear={handleSearchClear}
                disabled={isSearchDisabled}
              />
              {isSearchDisabled && comingSoonLabel ? (
                <span className={styles.comingSoonLabel}>{comingSoonLabel}</span>
              ) : null}
            </div>
          )}

          <div className={styles.actions}>
            {!searchSlot ? (
              <div className={searchButtonClasses}>
                <Button iconOnly size="l" variant="ghost" icon={Icon.SearchRegular} disabled={isSearchDisabled} />
              </div>
            ) : null}

            <div className={helpDesktopClasses}>
              <ShareFeedback
                open={feedbackPopoverOpen}
                onOpenChange={handleFeedbackPopoverOpenChange}
                anchor={<span aria-hidden="true" className={styles.helpDesktopAnchor} data-share-feedback-anchor />}
              >
                {isHelpDropdownActive ? (
                  <DropdownMenu
                    width="s"
                    align="end"
                    side="bottom"
                    sideOffset={4}
                    items={resolvedHelpItems ?? []}
                    onItemClick={onHelpClick}
                  >
                    <Button
                      iconOnly
                      size="l"
                      variant="ghost"
                      iconPlacement="right"
                      icon={Icon.QuestionCircleRegular}
                      className={styles.helpInvoker}
                      data-help-invoker
                    />
                  </DropdownMenu>
                ) : (
                  <Button
                    iconOnly
                    size="l"
                    variant="ghost"
                    iconPlacement="right"
                    icon={Icon.QuestionCircleRegular}
                    className={styles.helpInvoker}
                    data-help-invoker
                    disabled={isHelpButtonDisabled}
                  />
                )}
              </ShareFeedback>
            </div>

            <div className={helpMobileClasses}>
              <Button
                iconOnly
                size="l"
                variant="ghost"
                iconPlacement="right"
                icon={Icon.QuestionCircleRegular}
                className={styles.helpInvoker}
                data-help-invoker
                disabled={isHelpButtonDisabled || !hasHelpItems}
                onClick={() => setIsHelpDrawerOpen(true)}
              />
            </div>

            <div className={notificationClasses}>
              <div className={styles.notificationBtn}>
                <Button
                  iconOnly
                  size="l"
                  variant="ghost"
                  iconPlacement="right"
                  icon={Icon.AlertRegular}
                  onClick={onNotificationClick}
                  disabled={notificationsDisabled}
                />
                {!!notificationBadge && notificationBadge > 0 ? (
                  <span className={styles.notificationBadge} aria-hidden="true">
                    {notificationBadge}
                  </span>
                ) : null}
              </div>
            </div>

            <div className={styles.navigation}>
              <Button
                iconOnly
                size="l"
                variant="ghost"
                iconPlacement="right"
                icon={Icon.NavigationRegular}
                onClick={handleDrawerToggle}
              />
            </div>
          </div>

          <div className={styles.profile} data-testid={HEADER_TEST_IDS.profileMenu}>
            <DropdownMenu
              width="s"
              align="end"
              side="bottom"
              sideOffset={4}
              items={updatedProfileItems}
              onItemClick={onProfileClick}
            >
              <div className={styles.avatar}>
                {showHeaderAvatar ? <Avatar size={32} src={loadedAvatarSrc} /> : null}
              </div>
            </DropdownMenu>
          </div>
        </div>
      ) : null}

      {hasHelpItems ? (
        <HelpDrawer
          isOpen={isHelpDrawerOpen}
          onClose={() => setIsHelpDrawerOpen(false)}
          items={resolvedHelpItems ?? helpItems ?? []}
          onItemClick={onHelpClick}
        />
      ) : null}
    </Root>
  )
}
